import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {fetchDrives, fetchWaypoints, fetchZones} from "./mapObjectAPI";
import _ from "lodash";

function normalizeZonesPayload(payload) {
    if (Array.isArray(payload)) return payload;
    if (payload && Array.isArray(payload.models)) return payload.models;
    return [];
}

const initialState = {
    drives: [],
    waypoints: [],
    zones: [],
    geoObjectsInInfoWindow: [],
    loadDrivesInProcess: false,
    activeOrderId: null
};

export const loadDrivesAsync = createAsyncThunk(
    'drycleaning/drives',
    async (params= {}) => {
        const response = await fetchDrives(params);
        return response.data;
    }
);

export const loadWaypointsAsync = createAsyncThunk(
    'drycleaning/waypoints',
    async (params= {}) => {
        const response = await fetchWaypoints(params);
        return response.data;
    }
);

export const loadZonesAsync = createAsyncThunk(
    'drycleaning/zones',
    async (params= {}, thunkAPI) => {
        try {
            const response = await fetchZones(params);
            return response.data;
        } catch (e) {
            const status = e?.response?.status;
            if (status === 401 || status === 403) {
                return [];
            }
            return thunkAPI.rejectWithValue({
                message: e?.message,
                status,
                url: e?.config?.url,
            });
        }
    }
);

export const drycleaningMapObjectSlice = createSlice({
    name: 'list',
    initialState,
    reducers: {
        clearDrives(state) {
            state.drives = [];
        },
        clearWaypoints(state) {
            state.waypoints = [];
        },
        clearZones(state) {
            state.zones = [];
        },
        loadGeoObjectsInInfo(state, action) {
            state.geoObjectsInInfoWindow = action.payload;
        },
        saveOrderId(state, action) {
            state.activeOrderId = action.payload;
        },
        removeDrive(state, action) {
            state.drives = _.without(state.drives, _.find(state.drives, (drive) => (
                drive.id === action.payload.id
            )));
            state.geoObjectsInInfoWindow = _.without(state.geoObjectsInInfoWindow, _.find(state.geoObjectsInInfoWindow, (geoObject) => (
                geoObject.id === action.payload.id && !geoObject.isTransportation && !geoObject.isWaypoint
            )));
        },
        updateDrive(state, action) {
            const driveInState = _.find(state.drives, (drive) => (
                drive.id === action.payload.drive.id
            ))
            const driveInInfoWindow = _.find(state.geoObjectsInInfoWindow, (geoObject) => (
                geoObject.id === action.payload.drive.id && !geoObject.isWaypoint
            ))

            if (typeof driveInState !== "undefined") {
                const date = action.payload.attributes.date;
                if (typeof date !== "undefined" && date !== driveInState.date) {
                    state.drives = _.without(state.drives, driveInState);
                    state.geoObjectsInInfoWindow = _.without(state.geoObjectsInInfoWindow, driveInInfoWindow);
                }
                _.each(action.payload.attributes, (value, key) => {
                    driveInState[key] = value;
                });
            }

            if (typeof driveInInfoWindow !== "undefined") {
                _.each(action.payload.attributes, (value, key) => {
                    driveInInfoWindow[key] = value;
                })
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadDrivesAsync.pending, (state, action) => {
                state.loadDrivesInProcess = true;
            });
        builder
            .addCase(loadDrivesAsync.rejected, (state, action) => {
                state.loadDrivesInProcess = false;
            });
        builder
            .addCase(loadDrivesAsync.fulfilled, (state, action) => {
                state.loadDrivesInProcess = false;
                state.drives = action.payload;
            });
        builder
            .addCase(loadWaypointsAsync.fulfilled, (state, action) => {
                state.waypoints = action.payload;
            });
        builder
            .addCase(loadZonesAsync.rejected, (state, action) => {
                state.zones = [];
            });
        builder
            .addCase(loadZonesAsync.fulfilled, (state, action) => {
                state.zones = normalizeZonesPayload(action.payload);
            });
    },
});

export const {
    clearWaypoints,
    clearZones,
    clearDrives,
    loadGeoObjectsInInfo,
    saveOrderId,
    updateDrive,
    removeDrive} = drycleaningMapObjectSlice.actions;

export const selectDrives = (state) => state.drycleaningMapObject.drives;
export const selectWaypoints = (state) => state.drycleaningMapObject.waypoints;
export const selectZones = (state) => state.drycleaningMapObject.zones;
export const selectLoadDrivesInProcess = (state) => state.drycleaningMapObject.loadDrivesInProcess;
export const selectGeoObjectsInInfoWindow = (state) => state.drycleaningMapObject.geoObjectsInInfoWindow;

export const selectActiveOrderId = (state) =>  state.drycleaningMapObject.activeOrderId;

export default drycleaningMapObjectSlice.reducer;
